import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { requestMfa, verifyMfa } from '../services/login.service';

interface Props {
  userId: string;
  onSuccess: (token: string) => void;
  onResend: () => void;
}

const LoginMFA: React.FC<Props> = ({ userId, onSuccess, onResend }) => {
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const [email, setEmail] = useState('@');
  const hasUserInteractedRef = useRef(false);
  const hasFocusedRef = useRef(false);
    
useEffect(() => {
if (!userId) return;

  (async () => {
    try {
        const res = await requestMfa(userId);
        console.log(res);
        setEmail(res.data.data);
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.error ??
        err?.response?.data?.message ??
        "Error al enviar código";
          setError(apiMsg);
    }
  })();
}, [userId]);

// Maneja el temporizador
useEffect(() => {
  if (!canResend && resendTimer > 0) {
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  } else if (resendTimer === 0) {
    setCanResend(true);
  }
}, [resendTimer, canResend]);

// Efecto para autofocus en el primer dígito
useEffect(() => {
  if (!hasFocusedRef.current && !hasUserInteractedRef.current) {
    const timer = setTimeout(() => {
      const firstInput = inputsRef.current[0];
      if (firstInput && !hasUserInteractedRef.current) {
        firstInput.focus();
        hasFocusedRef.current = true;
      }
    }, 100);

    return () => clearTimeout(timer);
  }
}, []);

// Detectar interacción manual del usuario
useEffect(() => {
  const handleUserInteraction = (e: MouseEvent | TouchEvent | KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('mfa-digit')) {
      hasUserInteractedRef.current = true;
    }
  };

  document.addEventListener('click', handleUserInteraction);
  document.addEventListener('touchstart', handleUserInteraction);
  document.addEventListener('keydown', handleUserInteraction);

  return () => {
    document.removeEventListener('click', handleUserInteraction);
    document.removeEventListener('touchstart', handleUserInteraction);
    document.removeEventListener('keydown', handleUserInteraction);
  };
}, []);

  const handleChange = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newDigits = [...codeDigits];
      newDigits[index] = value;
      setCodeDigits(newDigits);

      if (value && inputsRef.current[index + 1]) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
  e.preventDefault();

  const raw = e.clipboardData.getData('text') ?? '';
  const pasted = raw.replace(/\D/g, '');
  if (!pasted) return;

  setCodeDigits((prev) => {
    const next = [...prev];
    const len = next.length;

    // Calcular desde dónde empezar a escribir
    let start: number;

    if (pasted.length >= len) {
      // Si me pegaron todo el código (o más), reemplazo completo desde 0
      start = 0;
    } else {
      // Intento desde el input actual; si no entra a la derecha, corro hacia la izquierda
      const maxStart = len - pasted.length;        // mayor índice inicial que permite que entren todos
      start = Math.min(Math.max(index, 0), maxStart);
    }

    // Escribir los dígitos dentro de bounds
    for (let i = 0; i < pasted.length && start + i < len; i++) {
      next[start + i] = pasted[i];
    }

    // Mover foco: si quedó algún vacío, al primero; si no, al último
    const firstEmpty = next.findIndex((d) => d === '');
    const target = firstEmpty === -1 ? len - 1 : firstEmpty;

    requestAnimationFrame(() => {
      const el = inputsRef.current?.[target];
      el?.focus();
      el?.select?.();
    });


    return next;
  });
};


  const handleVerify = async () => {
    const code = codeDigits.join('');
    if (code.length < 6) return;

    setLoading(true);
    try {
      const res = await verifyMfa({Login:userId, Code: code}); 
      if(res.data.validMFA){
      onSuccess(res.data.validMFA);
      }else{
        setError('Código incorrecto o expirado');
      }
    } catch {
      setError('Código inválido. Por favor, intenta nuevamente.');
      setCodeDigits(Array(6).fill(''));
      hasUserInteractedRef.current = false;
      hasFocusedRef.current = false;
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
  try {
    setError("");
    await onResend();
    setCanResend(false);
    setResendTimer(60);
    setCodeDigits(["", "", "", "", "", ""]);
    setCodeDigits(Array(6).fill(''));
    hasUserInteractedRef.current = false;
    hasFocusedRef.current = false;
    setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 100);
  } catch (err: any) {
    const apiMsg =
      err?.friendlyMessage ??
      err?.response?.data?.error ??    // contrato típico de error
      err?.response?.data?.message ??  // por si tu API usa "message"
      err?.message ??
      "No se pudo reenviar el código.";
    setError(apiMsg);
  } finally {
  }
};

const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
  e.currentTarget.select?.();
};

const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
  const { key, ctrlKey, metaKey } = e;

  // Permitir Ctrl/Cmd + V para que llegue el evento onPaste
  if ((ctrlKey || metaKey) && key.toLowerCase() === 'v') {
    return; // no hacemos preventDefault
  }

  if (key === 'Backspace') {
    e.preventDefault();
    setCodeDigits((prev) => {
      const next = [...prev];

      if (next[index]) {
        next[index] = '';
        return next;
      }

      const prevIndex = Math.max(0, index - 1);
      next[prevIndex] = '';
      requestAnimationFrame(() => inputsRef.current[prevIndex]?.focus());
      return next;
    });
    return;
  }

  if (key === 'Delete') {
    e.preventDefault();
    setCodeDigits((prev) => {
      const next = [...prev];
      next[index] = '';
      return next;
    });
    return;
  }

  if (key === 'ArrowLeft') {
    e.preventDefault();
    inputsRef.current[index - 1]?.focus();
    return;
  }

  if (key === 'ArrowRight') {
    e.preventDefault();
    inputsRef.current[index + 1]?.focus();
    return;
  }

  if (/^\d$/.test(key)) {
    e.preventDefault();
    handleChange(index, key);
    return;
  }

  if (key === 'Tab' || key === 'Home' || key === 'End') return;

  e.preventDefault();
};

  const focusAt = (idx: number) => {
    if (idx >= 0 && idx < codeDigits.length) {
      inputsRef.current[idx]?.focus();
      inputsRef.current[idx]?.select?.();
    }
  };
``

  return (
    <form className="formLoginEdenred" onSubmit={(e) => e.preventDefault()}>
      <input type="submit" hidden />
      <div className="login-input">
              <p className="d-block mb-3">Se envió un código al correo {email}</p>

     <div 
      className="d-flex justify-content-center mb-3"
      role="group"
      aria-label="Código de verificación de 6 dígitos"
    >
      {codeDigits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputsRef.current[index] = el!)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={(e) =>handlePaste(e,index)}         
            onFocus={handleFocus}        
            className="mfa-digit"
            aria-label={`Dígito ${index + 1} de 6`}
            aria-required="true"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            autoFocus={index === 0}
          />
        ))}
        </div>

            {error && (
              <div 
                className="p-error block mb-2" 
                role="alert"
                aria-live="polite"
              >
                {error}
              </div>
            )}

              <Button
                  label="VERIFICAR CÓDIGO"
                  type="button"
                  disabled={codeDigits.some((d) => d === '') || loading}
                  className={`btn btn-verify btn-block ${loading ? 'is-loading' : ''}`}
                  onClick={handleVerify}
                  aria-busy={loading}
              />


              <p className="timer mt-3" aria-live="polite">
                  {resendTimer > 0
                      ? `Si aún no recibiste el código, podrás solicitar uno nuevo en ${resendTimer}s`
                      : 'Ya podés solicitar un nuevo código'}
              </p>


              <Button
                  label="Reenviar código"
                  type="button"
                  disabled={!canResend}
                  className={`btn-resend ${canResend ? 'enabled' : ''}`}
                  onClick={handleResend}
                  aria-disabled={!canResend}
              />


      </div>
    </form>
  );
};

export default LoginMFA;